/**
 * The one definition of how a semantic colour scale is built.
 *
 * These percentages are consumed in three places that must never disagree:
 *
 *   tailwind.config.ts   emits the CSS `color-mix()` for every shade
 *   ThemeProvider.tsx    derives `-strong` against the same tint it will sit on
 *   ThemeTab.tsx         previews what a chosen colour will actually look like
 *
 * They were duplicated in all three, which meant a Settings preview could show
 * the admin a colour the app would never render. That is worse than no preview,
 * so the numbers live here and everything else imports them.
 *
 * This module is deliberately free of DOM and React so the Tailwind config, the
 * browser and the test runner can all load it.
 */

/** Soft tints: `color-mix(in srgb, base N%, surface)`. */
export const TINT_PERCENTS = {
  50: 8,
  100: 14,
  200: 28,
  300: 45,
  400: 70,
} as const;

/**
 * The heaviest tint a shade in the 50–100 range produces, as a 0–1 weight.
 * `-strong` is measured against this rather than against the bare surface,
 * because the real pattern is `bg-danger-100` + `text-danger-700`, not text on
 * a plain surface.
 */
export const TINT_BACKDROP = TINT_PERCENTS[100] / 100;

/**
 * Deep shades for hover and active states: `color-mix(in srgb, pole N%, strong)`.
 * Shades 800–950 used to be aliases of 700, which made `hover:bg-danger-800` on
 * a `bg-danger-700` button a no-op.
 */
export const DEEPEN_PERCENTS = {
  800: 12,
  900: 25,
  950: 40,
} as const;

/** Smallest sRGB channel shift a person reliably notices on a large filled area. */
export const MIN_HOVER_DELTA = 6;

/** WCAG AA threshold for normal-size text. */
export const AA_NORMAL = 4.5;

/** WCAG AA threshold for large text and non-text contrast. */
export const AA_LARGE = 3;

/** Is this a well-formed "R G B" triplet? */
export function isTriplet(value: string): boolean {
  return /^\d+\s+\d+\s+\d+$/.test(value.trim());
}

/** Linear blend of two "R G B" triplets. `weight` is how much of `b` to mix in. */
export function mixTriplets(a: string, b: string, weight: number): string {
  const pa = a.trim().split(/\s+/).map(Number);
  const pb = b.trim().split(/\s+/).map(Number);
  return pa.map((channel, i) => Math.round(channel + (pb[i] - channel) * weight)).join(" ");
}

/** Relative luminance of an "R G B" triplet, per WCAG 2.1. */
export function relativeLuminance(triplet: string): number {
  const [r, g, b] = triplet
    .trim()
    .split(/\s+/)
    .map((part) => {
      const value = Number.parseInt(part, 10) / 255;
      return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
    });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two "R G B" triplets. */
export function contrastRatio(a: string, b: string): number {
  const first = relativeLuminance(a);
  const second = relativeLuminance(b);
  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}

/** Convert a hex string or triplet to an "R G B" triplet, or null if unparseable. */
export function toTriplet(value: string): string | null {
  const raw = String(value ?? "").trim();
  if (isTriplet(raw)) return raw;
  const hex = raw.replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;
  return [
    Number.parseInt(hex.slice(0, 2), 16),
    Number.parseInt(hex.slice(2, 4), 16),
    Number.parseInt(hex.slice(4, 6), 16),
  ].join(" ");
}

/** Convert an "R G B" triplet to `#rrggbb`, or null if unparseable. */
export function toHex(triplet: string): string | null {
  if (!isTriplet(triplet)) return null;
  return `#${triplet
    .trim()
    .split(/\s+/)
    .map((p) => Number.parseInt(p, 10).toString(16).padStart(2, "0"))
    .join("")}`;
}

/** CSS `rgb()` string for an "R G B" triplet, for inline style previews. */
export function cssRgb(triplet: string): string {
  return `rgb(${triplet.trim().split(/\s+/).join(", ")})`;
}

/** The tinted background a shade in the 50–400 range resolves to. */
export function tintShade(base: string, surface: string, shade: keyof typeof TINT_PERCENTS): string {
  return mixTriplets(surface, base, TINT_PERCENTS[shade] / 100);
}

/* ── derivations ThemeProvider publishes as CSS variables ────────────────── */
/*
 * These were defined inside ThemeProvider.tsx. They are pure colour maths with
 * no React and no DOM, and they were moved here because ThemeProvider.tsx is a
 * "use client" module with JSX and path aliases, so no plain node script can
 * load it. The audit scripts therefore kept a hand-written *approximation* of
 * `strongVariant` — and it disagreed with the real one on the threshold
 * (3 vs AA_NORMAL), on the constraints (one backdrop vs two) and on the blend
 * range (60% vs 90%). Measurements taken against that copy described a product
 * that did not exist, and a scale was added to the Tailwind config to fix a
 * problem that was never there. Living here, the real function is loadable by
 * the audits, so that class of mistake cannot recur.
 *
 * ThemeProvider re-exports them all, so existing imports keep working.
 */

/**
 * A border strong enough to say "a control is here".
 *
 * `--color-border` is a DIVIDER. It separates table rows and edges cards, and
 * it is supposed to be quiet — measured across the presets it reaches 1.21:1
 * against the surface at worst, which is right for its job.
 *
 * It is the wrong colour for a BOUNDARY. WCAG 1.4.11 asks for 3:1 on anything
 * required to identify a control, and the shared Input/Select/Textarea fill
 * themselves with `--color-surface` — the same colour as the card behind them
 * (1.02:1). So both of the two ways a field can announce itself, its fill and
 * its outline, fail at the same time, and a text input in its resting state is
 * not visible as an input at all.
 *
 * The walk goes towards the TEXT colour rather than towards black or white,
 * which buys two things:
 *
 *   - the preset's hue survives: teal-tech's border is teal-tinted and stays
 *     teal-tinted, instead of turning grey like every other theme's;
 *   - clamping at the text colour means a field outline can never end up
 *     louder than the words inside it.
 *
 * Body text clears 12:1 against the surface in every preset, so there is always
 * room to reach 3:1 long before the walk runs out.
 *
 * Target is AA_LARGE, not AA_NORMAL: this is a line, not a sentence. Pushing it
 * to 4.5 was measured and produces a mid-grey outline around every field, which
 * reads as an error state rather than a resting one.
 *
 * `backdrops` is a list, not one colour, for the same reason `readableMuted`
 * takes a list: the token is painted on more than one background. It was
 * originally walked against `surface` alone, and measured 3.00:1 there — but
 * the page background is a shade further away, so the same token landed at
 * 2.98:1 on `bg-th-bg`. A control whose outline is guaranteed only on the
 * backdrop nobody put it on is guaranteed nowhere. Walking against the worst
 * of the three costs a barely perceptible darkening and makes the promise
 * true everywhere the class can be written.
 */
export function boundaryVariant(
  border: string,
  backdrops: string | string[],
  text: string,
  target: number = AA_LARGE,
): string {
  const list = (Array.isArray(backdrops) ? backdrops : [backdrops]).filter(isTriplet);
  if (!isTriplet(border) || list.length === 0 || !isTriplet(text)) return border;

  const clears = (candidate: string) =>
    list.every((backdrop) => contrastRatio(candidate, backdrop) >= target);

  if (clears(border)) return border;

  for (let mixIn = 5; mixIn <= 100; mixIn += 5) {
    const candidate = mixTriplets(border, text, mixIn / 100);
    if (clears(candidate)) return candidate;
  }
  return text;
}

const FOREGROUND_LIGHT = { triplet: "255 255 255", hex: "#ffffff" };
const FOREGROUND_DARK = { triplet: "15 23 42", hex: "#0f172a" };

/**
 * Secondary text, nudged until it is readable on every surface it lands on.
 *
 * `text-muted` is one of the last tokens a preset still states literally, and
 * that is exactly why it drifts. Every other text decision in the theme is
 * derived against the thing behind it; this one was chosen by eye against the
 * page background, where it comfortably passes, and then painted on surfaces
 * nobody re-checked.
 *
 * `surface-alt` is the surface it fails on, and it fails there by construction
 * rather than by accident. `surface-alt` is the one backdrop deliberately
 * shifted away from the page and towards the text, so that insets read as
 * recessed. Muted text is body text shifted towards the background. The two
 * tokens are defined as steps towards each other, so on some palettes they
 * simply meet: smart-blue lands at 4.34:1 in light mode and 4.04:1 in dark.
 *
 * Muted is a tone, not a licence. Secondary text is still text, and 4.5:1
 * still applies to it; "de-emphasised" describes how loud it should look, not
 * whether it has to be legible.
 *
 * So the value travels towards the text colour until it clears the bar against
 * the WORST surface it is used on, not merely the first one. Checking only one
 * backdrop is how the original value passed review in the first place.
 *
 * Presets that already clear the bar come back untouched, so this changes two
 * palettes and leaves the designer's intent alone everywhere else.
 */
/** State colours whose light tints muted text must stay readable on. */
export const MUTED_TINT_FAMILIES = ["danger", "success", "warning", "severe", "info", "primary", "secondary"] as const;
/** The tint rungs a callout or badge is painted with (`bg-X-50`, `bg-X-100`). */
export const MUTED_TINT_SHADES = [50, 100] as const;

/**
 * Every backdrop `text-th-text-muted` has to clear 4.5:1 on.
 *
 * The page, the card and the inset -- and, since 2026-09-28, the light tints
 * of every state colour. Muted text inside a tinted callout (`bg-danger-50`,
 * `bg-accent-50` rows, `bg-success-100` notes) is everywhere, and once the
 * audit could see ancestors across lines it found ~30 of them at 4.2-4.5:1.
 * The fix belongs HERE, not in thirty class strings: widening the set moves
 * muted just enough, and only on the palettes that need it (smart-blue light
 * and crimson dark; the other eight come back byte-identical).
 *
 * ONE list, used by ThemeProvider and by scripts/lib/theme-colour.js alike --
 * two copies of a backdrop list is how an audit ends up measuring a theme the
 * browser never paints.
 */
export function mutedBackdrops(colours: Readonly<Record<string, string | undefined>>): string[] {
  const out = [colours.bg, colours.surface, colours["surface-alt"]].filter(
    (v): v is string => typeof v === "string" && isTriplet(v),
  );
  const surface = colours.surface;
  if (surface && isTriplet(surface)) {
    for (const family of MUTED_TINT_FAMILIES) {
      const base = colours[family];
      if (!base || !isTriplet(base)) continue;
      for (const shade of MUTED_TINT_SHADES) out.push(tintShade(base, surface, shade));
    }
  }
  return out;
}

export function readableMuted(
  muted: string,
  surfaces: readonly string[],
  text: string,
  target: number = AA_NORMAL,
): string {
  if (!isTriplet(muted) || !isTriplet(text)) return muted;

  const backdrops = surfaces.filter(isTriplet);
  if (backdrops.length === 0) return muted;

  const clearsEverySurface = (candidate: string) =>
    backdrops.every((surface) => contrastRatio(candidate, surface) >= target);

  if (clearsEverySurface(muted)) return muted;

  for (let mixIn = 5; mixIn <= 100; mixIn += 5) {
    const candidate = mixTriplets(muted, text, mixIn / 100);
    if (clearsEverySurface(candidate)) return candidate;
  }
  return text;
}

/**
 * The hover shade of a surface: the same surface, moved just enough to be felt.
 *
 * Three things were reaching for this and finding nothing. `bg-th-surface-hover`
 * and `bg-th-bg-hover` were written in five places and generated no CSS at all,
 * because no such variable was ever published — so the rows and buttons using
 * them had no hover state whatsoever, silently.
 *
 * The direction has to be decided per mode, not fixed. "Darken on hover" is
 * only right on a light surface; on a dark one it moves the surface towards the
 * page and the element appears to sink out of the card. So the step travels
 * towards the text colour, which is dark in light mode and light in dark mode
 * by definition, and the theme therefore gets the direction right without
 * having to be told which mode it is in.
 *
 * `MIN_HOVER_DELTA` is the same threshold `deepPole` uses: below about six sRGB
 * units a hover reads as a rendering artefact rather than a response.
 */
export function surfaceHover(surface: string, text: string): string {
  if (!isTriplet(surface) || !isTriplet(text)) return surface;

  for (let mixIn = 2; mixIn <= 20; mixIn += 1) {
    const candidate = mixTriplets(surface, text, mixIn / 100);
    if (maxChannelDelta(surface, candidate) >= MIN_HOVER_DELTA) return candidate;
  }
  return mixTriplets(surface, text, 0.2);
}

/**
 * Pick white or near-black text for a given background colour, whichever
 * yields the higher contrast ratio. Falls back to white when the triplet
 * cannot be parsed, matching the original hard-coded behaviour.
 */
export function readableForeground(triplet: string): { triplet: string; hex: string } {
  if (!isTriplet(triplet)) return FOREGROUND_LIGHT;
  const onLight = contrastRatio(triplet, FOREGROUND_LIGHT.triplet);
  const onDark = contrastRatio(triplet, FOREGROUND_DARK.triplet);
  return onDark > onLight ? FOREGROUND_DARK : FOREGROUND_LIGHT;
}

/**
 * Derive a high-contrast variant of a state colour for use as a FILL, and for
 * text placed on a surface tinted with that same colour (`bg-danger-50` +
 * `text-danger-700`).
 *
 * The raw state colour is chosen to look right as a solid fill, so it is often
 * too close to the surface to read — `--color-warning` on white is the typical
 * failure. This walks the colour away from the surface (towards black on a
 * light surface, towards white on a dark one) until it clears AA against BOTH
 * the plain surface and the 100-tint of itself, so one value serves both modes.
 *
 * Returns the input unchanged when it already passes, and never blends past 90%
 * so the hue stays recognisable — which also means it can return a value that
 * misses the bar, when no blend within that range satisfies both constraints.
 */
export function strongVariant(base: string, surface: string, alsoReadOn: readonly string[] = []): string {
  if (!isTriplet(base) || !isTriplet(surface)) return base;

  // A light surface needs a darker colour, a dark surface needs a lighter one.
  const target = relativeLuminance(surface) > 0.5 ? "0 0 0" : "255 255 255";
  // Worst-case backdrop this colour will ever be read on.
  const backdrop = mixTriplets(surface, base, TINT_BACKDROP);
  const extra = alsoReadOn.filter(isTriplet);

  let candidate = base;
  for (let weight = 0; weight <= 0.9; weight += 0.05) {
    candidate = mixTriplets(base, target, weight);
    if (
      contrastRatio(candidate, surface) >= AA_NORMAL &&
      contrastRatio(candidate, backdrop) >= AA_NORMAL &&
      extra.every((b) => contrastRatio(candidate, b) >= AA_NORMAL)
    ) {
      return candidate;
    }
  }
  return candidate;
}

/**
 * THE `-700` of a state colour, for a whole palette. Every consumer -- the
 * runtime, the Settings preview, the audits -- calls this, so the set of
 * backdrops `-700` must clear is decided in exactly one place.
 *
 * `surface-alt` joined the set on 2026-09-28. Insets, table heads and chips
 * sit on it, and `text-danger-700` there measured 3.68:1 in olive-green dark:
 * `strongVariant` had only ever been checked against the card and its own
 * tint. Measured before the change: 7 of 70 preset/mode/family combinations
 * move, all dark, each by 1.04-1.23:1, and the label on the fill stays >= 7.77.
 */
export function strongFor(base: string, colours: Readonly<Record<string, string | undefined>>): string {
  const surface = colours.surface;
  if (!surface) return base;
  const alt = colours["surface-alt"];
  return strongVariant(base, surface, alt ? [alt] : []);
}

/** Must match the `800` step in `semanticScale()` in tailwind.config.ts. */
const HOVER_MIX = DEEPEN_PERCENTS[800] / 100;

function maxChannelDelta(a: string, b: string): number {
  const first = a.trim().split(/\s+/).map(Number);
  const second = b.trim().split(/\s+/).map(Number);
  return Math.max(...first.map((channel, i) => Math.abs(channel - second[i])));
}

/**
 * Choose which end of the range shades 800–950 should travel towards.
 *
 * Hover normally wants to move FURTHER from the surface — darker on a light
 * page, lighter on a dark one. But a button can already be pinned against that
 * end: in the olive-green preset the primary colour is nearly as dark as the
 * body text, so pushing it darker moves it about two sRGB units and the button
 * feels dead under the cursor.
 *
 * CSS `color-mix()` cannot make that decision because it cannot branch on
 * luminance, so the theme decides here and publishes the answer as a variable.
 * The config then just interpolates towards whatever pole it is handed, which
 * keeps the whole scale declarative and still leaves Settings in control.
 */
export function deepPole(strong: string, surface: string): string {
  const BLACK = "0 0 0";
  const WHITE = "255 255 255";
  const away = relativeLuminance(strong) < relativeLuminance(surface) ? BLACK : WHITE;
  if (maxChannelDelta(strong, mixTriplets(strong, away, HOVER_MIX)) >= MIN_HOVER_DELTA) return away;
  // No room left in that direction, so the hover has to move the other way.
  return away === BLACK ? WHITE : BLACK;
}

/** Absolute darkness ceiling for the scrim, used on light backgrounds. */
const SCRIM_MAX_LUMINANCE = 0.06;
/** How much darker than the background the scrim must be on dark backgrounds. */
const SCRIM_DARKEN_RATIO = 0.35;

/**
 * Build the modal / video-wall scrim for a given page background.
 *
 * A scrim only works if it is DARKER than whatever it covers. An absolute
 * "make it dark" rule is not enough: a near-black overlay is still lighter than
 * a dark preset's `--color-bg`, so the backdrop would glow when a modal opens.
 *
 * So the target is relative: darken the background's own colour (keeping its
 * hue) until it is comfortably below the background's luminance, and never
 * above an absolute ceiling. The result is used as `bg-th-overlay/40`.
 */
export function scrimColor(bg: string): string {
  if (!isTriplet(bg)) return "15 23 42";

  const ceiling = Math.min(SCRIM_MAX_LUMINANCE, relativeLuminance(bg) * SCRIM_DARKEN_RATIO);

  let candidate = bg;
  for (let weight = 0; weight <= 1; weight += 0.02) {
    candidate = mixTriplets(bg, "0 0 0", weight);
    if (relativeLuminance(candidate) <= ceiling) return candidate;
  }
  return "0 0 0";
}
